import React, { useEffect, useState } from "react";
import classes from "./OrganizationSettings.module.css";
import axios from "axios";
import Swal from "sweetalert2";
import api, { withCompanyId } from "../../Components/API/Api";
import { useNavigate } from "react-router-dom";
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import { useUser } from '../../UserContext';
import { Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { normalizeAutoVatSetting } from './organizationSettingsUtils';

const OrganizationSettings = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useUser();
  const [formData, setFormData] = useState({
    orgName: "",
    email: "",
    phone: "",
    address: "",
    industry: "",
    taxId: "",
    accountName: "",
    accountNumber: "",
    bankName: "",
    currency: "NGN",
    timezone: "Africa/Lagos",
    notes: "",
    // new payroll-related settings
    payroll_computation_method: "", // 1 for Direct, 2 for Step-Grade
    payee_remittance: "", // tax agency id
    wallet_account: "", // petty cash account
    wallet_bank_account: "", // bank account
    pay_day: "", // date string
    payment_approval_workflow: "1", // 1 for Deactivate, 2 for Activate
    auto_vat_deduction: "0",
    accountDetails: [{ id: Date.now(), accountName: "", accountNumber: "", bankName: "", isDefault: true }],
    logoDataUrl: "",
    logoFile: null,
  });

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [loadingTimezones, setLoadingTimezones] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [taxAgencies, setTaxAgencies] = useState([]);
  const [loadingTaxAgencies, setLoadingTaxAgencies] = useState(false);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [loadingCashAccounts, setLoadingCashAccounts] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [companyBankAccounts, setCompanyBankAccounts] = useState([]);
  const [loadingCompanyBankAccounts, setLoadingCompanyBankAccounts] = useState(false);
  const [defaultBankAccountId, setDefaultBankAccountId] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [payeeTaxAgents, setPayeeTaxAgents] = useState([{ id: Date.now(), tax_agency_id: "", tax_id: "", validation_status: null, validation_message: "" }]);
  const [companyId, setCompanyId] = useState(null);
  const [taxAgentValidationStatus, setTaxAgentValidationStatus] = useState({});
  const [type, setType] = useState("generalSettings"); // Changed from "products" to "product"
  const [incomeAccounts, setIncomeAccounts] = useState([]);
  const [cogsAccounts, setCogsAccounts] = useState([]);
  const [inventoryAccounts, setInventoryAccounts] = useState([]);
  const [taxTypes, setTaxTypes] = useState([]);
  const [payableAccounts, setPayableAccounts] = useState([]);
  const [receivableAccounts, setReceivableAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  // Payment Approval Workflow Modal State
  const [showPaymentWorkflowModal, setShowPaymentWorkflowModal] = useState(false);
  const [workflowLevels, setWorkflowLevels] = useState([{ id: Date.now(), role_id: "" }]);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [savingWorkflow, setSavingWorkflow] = useState(false);

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setLoadingBanks(true);
        const res = await api.get("/v1/paystack/banks");

        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        setBanks(list || []);

        if (list && list.length) {
          // if no bank selected yet, try to pick from originalData or default to first
          setFormData((prev) => {
            if (prev.bankName) return prev;
            const match = originalData && originalData.bank_name
              ? list.find((b) => ((b.name || "").toLowerCase() === (originalData.bank_name || "").toLowerCase()) || (String(b.code || "") === String(originalData.bank_code || "")))
              : null;
            return { ...prev, bankName: match ? (match.name || "") : (prev.bankName || (list[0].name || "")) };
          });
        }
      } catch (err) {
        console.error("Failed to fetch banks", err);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchBanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchTaxAgencies = async () => {
      try {
        setLoadingTaxAgencies(true);
        const res = await api.get("/v1/taxagency");

        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        setTaxAgencies(list || []);
      } catch (err) {
        console.error("Failed to fetch tax agencies", err);
        setTaxAgencies([]);
      } finally {
        setLoadingTaxAgencies(false);
      }
    };

    fetchTaxAgencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCashAccounts = async () => {
      try {
        setLoadingCashAccounts(true);
        const res = await api.get("/v1/cash");

        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        setCashAccounts(list || []);
      } catch (err) {
        console.error("Failed to fetch cash accounts", err);
        setCashAccounts([]);
      } finally {
        setLoadingCashAccounts(false);
      }
    };

    fetchCashAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        setLoadingBankAccounts(true);
        const res = await api.get("/v1/bank-accounts");

        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        setBankAccounts(list || []);
      } catch (err) {
        console.error("Failed to fetch bank accounts", err);
        setBankAccounts([]);
      } finally {
        setLoadingBankAccounts(false);
      }
    };

    fetchBankAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCompanyBankAccounts = async () => {
      try {
        setLoadingCompanyBankAccounts(true);
        const res = await api.get("/v1/companies/get_bank_accounts");

        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        setCompanyBankAccounts(list || []);
        
        console.log("Company bank accounts fetched:", list);
        
        // Update accountDetails in formData with all fetched bank accounts
        if (list && list.length > 0) {
          const accountDetails = list.map((account, index) => ({
            id: account.id || Date.now() + index,
            accountName: account.account_name || "",
            accountNumber: account.account_number || "",
            bankName: account.bank_name || "",
            isDefault: account.default === 1 || account.default === true
          }));
          setFormData((prev) => ({
            ...prev,
            accountDetails: accountDetails
          }));
          
          // Set the default bank account ID from the response
          const defaultAccount = list.find((account) => account.default === 1 || account.default === true);
          if (defaultAccount) {
            setDefaultBankAccountId(defaultAccount.id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch company bank accounts", err);
        setCompanyBankAccounts([]);
      } finally {
        setLoadingCompanyBankAccounts(false);
      }
    };

    fetchCompanyBankAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const res = await api.get("/v1/payroll/role", {
          params: { name: "" }
        });

        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        setRoles(list || []);
      } catch (err) {
        console.error("Failed to fetch roles", err);
        setRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        setLoading(true);
        const companyIdHeader = localStorage.getItem('company_id') || localStorage.getItem('activeCompanyId') || null;
        
        if (!companyIdHeader) {
          console.warn("No company_id found, cannot fetch company info");
          setLoading(false);
          return;
        }

        const res = await api.get("/v1/companies/get-info", {
          headers: {
            company_id: companyIdHeader,
          },
        });

        const items = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const activeId = localStorage.getItem('company_id') || localStorage.getItem('activeCompanyId') || null;
        const item = Array.isArray(items) && items.length
          ? (activeId ? items.find(c => String(c.id) === String(activeId)) || items[0] : items[0])
          : null;

        if (item) {
          setOriginalData(item);
          setCompanyId(item.id);
          setFormData((prev) => ({
            ...prev,
            orgName: item.name || "",
            email: item.email || "",
            phone: item.phone_number || "",
            address: item.address || "",
            industry: item.industry || "",
            accountName: item.account_name || "",
            accountNumber: item.account_number || "",
            bankName: item.bank_name || "",
            taxId: item.vat_registration || "",
            currency: item.currency_base || prev.currency || "NGN",
            timezone: item.time_zone || prev.timezone || "Africa/Lagos",
            notes: item.note || prev.notes || "",
            payroll_computation_method: item.payroll_computation_method || prev.payroll_computation_method || "",
            payee_remittance: item.payee_remittance || prev.payee_remittance || "",
            wallet_account: item.wallet_account || prev.wallet_account || "",
            wallet_bank_account: item.wallet_bank_account || prev.wallet_bank_account || "",
            pay_day: item.pay_day || prev.pay_day || "",
            payment_approval_workflow: item.payment_approval_workflow || prev.payment_approval_workflow || "1",
            auto_vat_deduction: item.auto_vat_deduction != null ? String(item.auto_vat_deduction) : prev.auto_vat_deduction || "0",
            accountDetails: (() => {
              let details = [{ id: Date.now(), accountName: item.account_name || "", accountNumber: item.account_number || "", bankName: item.bank_name || "", isDefault: true }];
              if (item.account_details) {
                try {
                  const parsed = typeof item.account_details === "string" ? JSON.parse(item.account_details) : item.account_details;
                  if (Array.isArray(parsed) && parsed.length) {
                    details = parsed.map((row, index) => ({
                      id: row.id || Date.now() + index,
                      accountName: row.accountName || row.account_name || "",
                      accountNumber: row.accountNumber || row.account_number || "",
                      bankName: row.bankName || row.bank_name || "",
                      isDefault: !!row.isDefault || !!row.default,
                    }));
                    if (!details.some((row) => row.isDefault)) {
                      details[0].isDefault = true;
                    }
                  }
                } catch (err) {
                  console.error("Failed to parse account_details", err);
                }
              }
              return details;
            })(),
            logoDataUrl: item.logo || prev.logoDataUrl || "",
          }));

          // Load payee tax agents if available
          if (item.payee_tax_agents) {
            try {
              const agents = typeof item.payee_tax_agents === 'string' 
                ? JSON.parse(item.payee_tax_agents)
                : item.payee_tax_agents;
              if (Array.isArray(agents) && agents.length > 0) {
                setPayeeTaxAgents(agents.map((agent, idx) => ({
                  id: Date.now() + idx,
                  tax_agency_id: agent.tax_agency_id || "",
                  tax_id: agent.tax_id || "",
                  validation_status: null,
                  validation_message: ""
                })));
              }
            } catch (e) {
              console.error("Failed to parse payee_tax_agents", e);
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch company info", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyInfo();
  }, []);

  const fetchExistingTaxAgents = async (id = companyId) => {
    if (!id) {
      console.warn("Company ID not available for fetching tax agents");
      return;
    }
    try {
      const res = await api.get("/v1/company_tax_agent/fetch", {
        params: { company_id: id }
      });

      const agents = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
      
      console.log("Fetched tax agents from endpoint:", agents);
      
      if (agents && agents.length > 0) {
        const mappedAgents = agents.map((agent, idx) => ({
          id: Date.now() + idx,
          tax_agency_id: agent.tax_agent_id || agent.tax_agency_id || "",
          tax_id: agent.stin || agent.tax_id || "",
          validation_status: null,
          validation_message: ""
        }));
        console.log("Mapped tax agents:", mappedAgents);
        setPayeeTaxAgents(mappedAgents);
      } else {
        // If no agents found, keep default empty row
        console.log("No tax agents found, keeping default empty row");
        setPayeeTaxAgents([{ id: Date.now(), tax_agency_id: "", tax_id: "", validation_status: null, validation_message: "" }]);
      }
    } catch (err) {
      console.error("Failed to fetch existing tax agents:", err);
      // On error, keep the current state or show default
      setPayeeTaxAgents([{ id: Date.now(), tax_agency_id: "", tax_id: "", validation_status: null, validation_message: "" }]);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchExistingTaxAgents(companyId);
    }
  }, [companyId]);

  const handleExport = () => {
    const exportData = {
      orgName: formData.orgName,
      industry: formData.industry,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      currency: formData.currency,
      timezone: formData.timezone,
      notes: formData.notes,
      payroll_computation_method: formData.payroll_computation_method,
      payee_remittance: formData.payee_remittance,
      pay_day: formData.pay_day,
      payment_approval_workflow: formData.payment_approval_workflow,
      auto_vat_deduction: formData.auto_vat_deduction,
    };
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'organization-profile.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    if (!window.confirm('Reset all fields to last saved values?')) return;
    if (originalData) {
      setFormData((prev) => ({
        ...prev,
        orgName: originalData.name || "",
        email: originalData.email || "",
        phone: originalData.phone_number || "",
        address: originalData.address || "",
        industry: originalData.industry || "",
        accountName: originalData.account_name || "",
        accountNumber: originalData.account_number || "",
        bankName: originalData.bank_name || "",
        taxId: originalData.vat_registration || "",
        currency: originalData.currency_base || prev.currency,
        timezone: originalData.time_zone || prev.timezone,
        notes: originalData.note || prev.notes,
        payroll_computation_method: originalData.payroll_computation_method || prev.payroll_computation_method || "",
        payee_remittance: originalData.payee_remittance || prev.payee_remittance || "",
        wallet_account: originalData.wallet_account || prev.wallet_account || "",
        wallet_bank_account: originalData.wallet_bank_account || prev.wallet_bank_account || "",
        pay_day: originalData.pay_day || prev.pay_day || "",
        payment_approval_workflow: originalData.payment_approval_workflow || prev.payment_approval_workflow || "1",
        auto_vat_deduction: originalData.auto_vat_deduction != null ? String(originalData.auto_vat_deduction) : prev.auto_vat_deduction || "0",
        logoDataUrl: originalData.logo || "",
        logoFile: null,
      }));
      
      // Reset payee tax agents
      if (originalData.payee_tax_agents) {
        try {
          const agents = typeof originalData.payee_tax_agents === 'string' 
            ? JSON.parse(originalData.payee_tax_agents)
            : originalData.payee_tax_agents;
          if (Array.isArray(agents) && agents.length > 0) {
            setPayeeTaxAgents(agents.map((agent, idx) => ({
              id: Date.now() + idx,
              tax_agency_id: agent.tax_agency_id || "",
              tax_id: agent.tax_id || "",
              validation_status: null,
              validation_message: ""
            })));
          } else {
            setPayeeTaxAgents([{ id: Date.now(), tax_agency_id: "", tax_id: "", validation_status: null, validation_message: "" }]);
          }
        } catch (e) {
          setPayeeTaxAgents([{ id: Date.now(), tax_agency_id: "", tax_id: "", validation_status: null, validation_message: "" }]);
        }
      } else {
        setPayeeTaxAgents([{ id: Date.now(), tax_agency_id: "", tax_id: "", validation_status: null, validation_message: "" }]);
      }
    } else {
      // clear
      setFormData({
        orgName: "",
        email: "",
        phone: "",
        address: "",
        industry: "",
        accountName: "",
        accountNumber: "",
        bankName: "",
        taxId: "",
        currency: "NGN",
        timezone: "Africa/Lagos",
        notes: "",
        payroll_computation_method: "",
        payee_remittance: "",
        wallet_account: "",
        wallet_bank_account: "",
        pay_day: "",
        payment_approval_workflow: "1",
        auto_vat_deduction: "0",
        logoDataUrl: "",
        logoFile: null,
      });
      setPayeeTaxAgents([{ id: Date.now(), tax_agency_id: "", tax_id: "", validation_status: null, validation_message: "" }]);
    }
  };

  const handleCancel = async () => {
      const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: 'Discard changes and go back?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#2d995f',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, discard!',
      cancelButtonText: 'No, cancel',
    });

    if (!confirmed.isConfirmed) {
      return; // User canceled, do nothing
    }
      navigate('/settings/product-services');
  };

  useEffect(() => {
    const fetchTimezones = async () => {
      try {
        setLoadingTimezones(true);
        const res = await api.get("/v1/get-time-zone");

        const list = Array.isArray(res?.data?.data) ? res.data.data : res?.data;
        const names = Array.isArray(list)
          ? list.map((it) => (typeof it === "string" ? it : (it?.name || ""))).filter(Boolean)
          : [];
        setTimezoneOptions(names);

        if (names.length) {
          const hasCurrent = names.includes(formData.timezone);
          if (!hasCurrent) {
            const fallback = names.includes("Africa/Lagos") ? "Africa/Lagos" : names[0];
            setFormData((prev) => ({ ...prev, timezone: fallback }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch timezones", err);
      } finally {
        setLoadingTimezones(false);
      }
    };

    fetchTimezones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        setLoadingCurrencies(true);
        const res = await api.get("/v1/fetch-all-currencies");

        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const options = list
          .map((c) => ({
            id: c.id,
            code: c.code || c.name,
            name: c.name || c.code,
            symbol: c.symbol || "",
            country: c.country || "",
          }))
          .filter((c) => c.id != null);
        setCurrencyOptions(options);

        if (options.length) {
          const current = formData.currency;
          const currentStr = (current ?? "").toString().trim();

          let match = options.find((o) => String(o.id) === currentStr);
          if (!match) match = options.find((o) => (o.code || "").toString().toUpperCase() === currentStr.toUpperCase());
          if (!match) match = options.find((o) => (o.symbol || "").toString().toUpperCase() === currentStr.toUpperCase());
          if (!match) match = options.find((o) => (o.country || "").toString().toUpperCase() === currentStr.toUpperCase());

          if (match) {
            setFormData((prev) => ({ ...prev, currency: match.id }));
          } else {
            const ngn = options.find(
              (o) => (o.code || "").toUpperCase() === "NGN" || (o.symbol || "").includes("₦") || (o.country || "").toLowerCase().includes("nigeria")
            );
            if (ngn) setFormData((prev) => ({ ...prev, currency: ngn.id }));
            else setFormData((prev) => ({ ...prev, currency: options[0].id }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch currencies", err);
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchCurrencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setLoadingIndustries(true);
        const res = await api.get("/v1/industry");

        const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        setIndustries(list || []);
      } catch (err) {
        console.error("Failed to fetch industries", err);
        setIndustries([]);
      } finally {
        setLoadingIndustries(false);
      }
    };

    fetchIndustries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangedd = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "payment_approval_workflow") {
      setFormData((p) => ({
        ...p,
        [name]: type === "checkbox" ? checked : value
      }));
      // Open modal if "Activate" (value 2) is selected
      if (value === "2") {
        setShowPaymentWorkflowModal(true);
      }
    } else {
      setFormData((p) => ({
        ...p,
        [name]: type === "checkbox" ? checked : value
      }));
    }
  };

  const handleAddAccountDetailRow = () => {
    setFormData((prev) => ({
      ...prev,
      accountDetails: [
        ...prev.accountDetails,
        { id: Date.now(), accountName: "", accountNumber: "", isDefault: prev.accountDetails.length === 0 }
      ],
    }));
  };

  const handleAccountDetailChange = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      accountDetails: prev.accountDetails.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleAccountDetailDefault = async (id) => {
    setFormData((prev) => ({
      ...prev,
      accountDetails: prev.accountDetails.map((item) => ({
        ...item,
        isDefault: item.id === id,
      })),
    }));
    
    // Trigger the set default bank account endpoint
    await handleSetDefaultBankAccount(id);
  };

  const handleRemoveAccountDetailRow = (id) => {
    setFormData((prev) => {
      const updated = prev.accountDetails.filter((item) => item.id !== id);
      if (!updated.length) {
        return {
          ...prev,
          accountDetails: [{ id: Date.now(), accountName: "", accountNumber: "", isDefault: true }],
        };
      }
      if (!updated.some((item) => item.isDefault)) {
        updated[0] = { ...updated[0], isDefault: true };
      }
      return { ...prev, accountDetails: updated };
    });
  };

  const getAccountDetailsByPayload = (details, fallbackName, fallbackNumber, fallbackBankName) => {
    let accountDetails = Array.isArray(details) && details.length ? details : [{ id: Date.now(), accountName: fallbackName || "", accountNumber: fallbackNumber || "", bankName: fallbackBankName || "", isDefault: true }];
    accountDetails = accountDetails.map((row, index) => ({
      id: row.id || Date.now() + index,
      accountName: row.accountName || row.account_name || "",
      accountNumber: row.accountNumber || row.account_number || "",
      bankName: row.bankName || row.bank_name || "",
      isDefault: !!row.isDefault || !!row.default,
    }));
    if (!accountDetails.some((row) => row.isDefault) && accountDetails.length) {
      accountDetails[0].isDefault = true;
    }
    return accountDetails;
  };

  const handleSetDefaultBankAccount = async (bankAccountId) => {
    try {
      const res = await api.get("/v1/companies/set_default_bank_accounts", {
        params: {
          bank_account_id: bankAccountId
        }
      });

      const success = res?.data?.status === true || res?.data?.success === true;

      if (success) {
        setDefaultBankAccountId(bankAccountId);
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: res?.data?.message || "Default bank account set successfully",
          confirmButtonColor: "#16a34a",
        });
        // Refresh the company bank accounts to reflect the change
        const refreshRes = await api.get("/v1/companies/get_bank_accounts");
        const list = Array.isArray(refreshRes?.data?.data) ? refreshRes.data.data : Array.isArray(refreshRes?.data) ? refreshRes.data : [];
        setCompanyBankAccounts(list || []);
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.data?.message || "Failed to set default bank account",
          confirmButtonColor: "#16a34a",
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || error?.message || "Failed to set default bank account",
        confirmButtonColor: "#16a34a",
      });
    }
  };

  const handleAddLevel = () => {
    setWorkflowLevels((prev) => [...prev, { id: Date.now(), role_id: "" }]);
  };

  const handleRemoveLevel = (levelId) => {
    if (workflowLevels.length === 1) {
      Swal.fire({
        icon: "warning",
        title: "Cannot Remove",
        text: "You must have at least one approval level.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }
    setWorkflowLevels((prev) => prev.filter((level) => level.id !== levelId));
  };

  const handleLevelRoleChange = (levelId, roleId) => {
    setWorkflowLevels((prev) =>
      prev.map((level) =>
        level.id === levelId ? { ...level, role_id: roleId } : level
      )
    );
  };

  const handleSaveWorkflow = async () => {
    // Validate that all levels have roles selected
    const invalidLevels = workflowLevels.filter((level) => !level.role_id);
    if (invalidLevels.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Data",
        text: "Please ensure all approval levels have a role selected.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }

    try {
      setSavingWorkflow(true);
      const roleIds = workflowLevels.map((level) => level.role_id);

      const res = await api.post("/v1/payment_approval/create_workflow", {
        company_id: companyId,
        role_id: roleIds,
      });

      const success = res?.data?.status === true || res?.data?.success === true;

      if (success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: res?.data?.message || "Payment approval workflow saved successfully",
          confirmButtonColor: "#16a34a",
        });
        setShowPaymentWorkflowModal(false);
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.data?.message || "Failed to save payment approval workflow",
          confirmButtonColor: "#16a34a",
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || error?.message || "Failed to save payment approval workflow",
        confirmButtonColor: "#16a34a",
      });
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleCancelWorkflow = () => {
    setShowPaymentWorkflowModal(false);
    setWorkflowLevels([{ id: Date.now(), role_id: "" }]);
    // Reset the dropdown to deactivate
    setFormData((prev) => ({ ...prev, payment_approval_workflow: "1" }));
  };

  
  
  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert("File too large — max 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setFormData((prev) => ({ ...prev, logoDataUrl: String(ev.target?.result || ""), logoFile: file }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setFormData((prev) => ({ ...prev, logoDataUrl: "", logoFile: null }));
  };

  const handleAddTaxAgentRow = () => {
    setPayeeTaxAgents((prev) => [...prev, { id: Date.now(), tax_agency_id: "", tax_id: "", validation_status: null, validation_message: "" }]);
  };

  const handleRemoveTaxAgentRow = (rowId) => {
    if (payeeTaxAgents.length === 1) {
      Swal.fire({
        icon: "warning",
        title: "Cannot Remove",
        text: "You must have at least one tax agent row.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }
    setPayeeTaxAgents((prev) => prev.filter((row) => row.id !== rowId));
  };

  const clearValidationMessage = (rowId) => {
    setPayeeTaxAgents((prev) => prev.map((agent) => {
      if (agent.id === rowId) {
        return {
          ...agent,
          validation_message: "",
          validation_status: null
        };
      }
      return agent;
    }));
  };

  const handleTaxAgentChange = (rowId, field, value) => {
    setPayeeTaxAgents((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = { ...row, [field]: value };
          
          // Clear validation message if tax_id is cleared
          if (field === "tax_id" && !value) {
            updatedRow.validation_status = null;
            updatedRow.validation_message = "";
          }
          
          return updatedRow;
        }
        return row;
      })
    );
    
    // Trigger validation if tax_id field is changed and reaches 10 digits
    if (field === "tax_id" && value.length === 10 && /^\d+$/.test(value)) {
      validateTaxId(rowId, value);
    }
  };

  const validateTaxId = async (rowId, taxId) => {
    if (!taxId || taxId.length !== 10 || !/^\d+$/.test(taxId)) {
      return;
    }
    
    const row = payeeTaxAgents.find((r) => r.id === rowId);
    
    if (!row || !row.tax_agency_id) {
      setTaxAgentValidationStatus((prev) => ({
        ...prev,
        [rowId]: { 
          loading: false, 
          status: "error",
          message: "Please select a tax agent first"
        }
      }));
      return;
    }

    // Check if the selected tax agency is "Ogun state internal revenue service"
    const selectedAgency = taxAgencies.find((agency) => String(agency.id) === String(row.tax_agency_id));
    
    const agencyName = selectedAgency?.name || selectedAgency?.agency_name || "";
    const isOgunAgency = agencyName.toLowerCase().includes("ogun");
    
    if (!selectedAgency || !isOgunAgency) {
      setTaxAgentValidationStatus((prev) => ({
        ...prev,
        [rowId]: { 
          loading: false, 
          status: "invalid",
          message: "✗ Validation only available for Ogun State Internal Revenue Service"
        }
      }));
      setPayeeTaxAgents((prev) =>
        prev.map((r) => 
          r.id === rowId 
            ? { 
                ...r, 
                validation_status: "invalid",
                validation_message: "✗ Validation only available for Ogun State Internal Revenue Service"
              } 
            : r
        )
      );
      return;
    }
    
    try {
      setTaxAgentValidationStatus((prev) => ({
        ...prev,
        [rowId]: { loading: true, message: "Validating..." }
      }));

      const res = await api.get("/v1/company_tax_agent/validate_tin", {
        params: {
          company_id: companyId,
          tax_agent_id: row.tax_agency_id,
          stin: taxId
        }
      });

      const isValid = res?.data?.status === true || res?.data?.success === true;
      
      console.log("Tax ID validation response - isValid:", isValid);
      
      setPayeeTaxAgents((prev) =>
        prev.map((row) => 
          row.id === rowId 
            ? { 
                ...row, 
                validation_status: isValid ? "valid" : "invalid",
                validation_message: isValid ? "Tax ID validated successfully" : "Tax ID does not exist"
              } 
            : row
        )
      );

      setTaxAgentValidationStatus((prev) => ({
        ...prev,
        [rowId]: { 
          loading: false, 
          status: isValid ? "valid" : "invalid",
          message: isValid ? "✓ Tax ID validated successfully" : "✗ Tax ID does not exist"
        }
      }));
    } catch (err) {
      setPayeeTaxAgents((prev) =>
        prev.map((row) => 
          row.id === rowId 
            ? { 
                ...row, 
                validation_status: "error",
                validation_message: "Validation error. Tax ID does not exist. Generate a tax id."
              } 
            : row
        )
      );

      setTaxAgentValidationStatus((prev) => ({
        ...prev,
        [rowId]: { 
          loading: false, 
          status: "error",
          message: "Validation error. Tax ID does not exist. Generate a tax id."
        }
      }));
    }
  };

  const handleGenerateTaxId = async (rowId) => {
    const row = payeeTaxAgents.find((r) => r.id === rowId);
    if (!row || !row.tax_agency_id) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please select a tax agent first.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }

    // Check if the selected tax agency is "Ogun state internal revenue service"
    const selectedAgency = taxAgencies.find((agency) => String(agency.id) === String(row.tax_agency_id));
    const agencyName = selectedAgency?.name || selectedAgency?.agency_name || "";
    const isOgunAgency = agencyName.toLowerCase().includes("ogun");
    
    if (!selectedAgency || !isOgunAgency) {
      Swal.fire({
        icon: "warning",
        title: "Not Available",
        text: "Tax ID generation is only available for Ogun State Internal Revenue Service.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }

    try {
      const res = await api.get("/v1/company_tax_agent/create_tin", {
        params: {
          company_id: companyId,
          tax_agent_id: row.tax_agency_id
        }
      });

      const generatedId = res?.data?.data || res?.data?.stin;
      
      if (generatedId) {
        handleTaxAgentChange(rowId, "tax_id", generatedId);
        
        await Swal.fire({
          icon: "success",
          title: "Generated",
          text: `Tax ID generated: ${generatedId}`,
          confirmButtonColor: "#16a34a",
        });

        // Validate the newly generated tax ID
        validateTaxId(rowId, generatedId);
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to generate Tax ID. Please try again.",
          confirmButtonColor: "#16a34a",
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || error?.message || "Failed to generate tax ID",
        confirmButtonColor: "#16a34a",
      });
    }
  };

  const generateTaxIdForRow = async (rowId) => {
    const row = payeeTaxAgents.find((r) => r.id === rowId);
    if (!row || !row.tax_agency_id) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please select a tax agent first.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }

    try {
      const res = await api.get("/v1/company_tax_agent/create_tin", {
        params: {
          company_id: companyId,
          tax_agent_id: row.tax_agency_id
        }
      });

      const generatedId = res?.data?.data || res?.data?.stin;
      
      if (generatedId) {
        handleTaxAgentChange(rowId, "tax_id", generatedId);
        
        await Swal.fire({
          icon: "success",
          title: "Generated",
          text: `Tax ID generated: ${generatedId}`,
          confirmButtonColor: "#16a34a",
        });

        // Validate the newly generated tax ID
        validateTaxId(rowId, generatedId);
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to generate Tax ID. Please try again.",
          confirmButtonColor: "#16a34a",
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || error?.message || "Failed to generate tax ID",
        confirmButtonColor: "#16a34a",
      });
    }
  };

  const submitTaxAgents = async () => {
    // Validate that all rows have tax_agent_id and stin
    const invalidRows = payeeTaxAgents.filter(row => !row.tax_agency_id || !row.tax_id);
    if (invalidRows.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Data",
        text: "Please ensure all rows have a tax agent selected and a tax ID entered.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }

    try {
      const taxAgentIds = payeeTaxAgents.map(row => String(row.tax_agency_id));
      const stins = payeeTaxAgents.map(row => row.tax_id);

      const res = await api.post("/v1/company_tax_agent/create", {
        company_id: companyId,
        tax_agent_id: taxAgentIds,
        stin: stins
      });

      const success = res?.data?.status === true || res?.data?.success === true;
      
      if (success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: res?.data?.message || "Tax agents submitted successfully",
          confirmButtonColor: "#16a34a",
        });
        
        // Refresh tax agents from the server to ensure latest data is displayed
        console.log("Refreshing tax agents after successful submission");
        await fetchExistingTaxAgents(companyId);
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.data?.message || "Failed to submit tax agents",
          confirmButtonColor: "#16a34a",
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || error?.message || "Failed to submit tax agents",
        confirmButtonColor: "#16a34a",
      });
    }
  };

  const handleSubmitOld = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      const form = new FormData();
      form.append("name", formData.orgName || "");
      form.append("email", formData.email || "");
      form.append("phone_number", formData.phone || "");
      form.append("address", formData.address || "");
      form.append("account_name", formData.accountName || "");
      form.append("account_number", formData.accountNumber || "");
      form.append("bank_name", formData.bankName || "");
      if (formData.industry) form.append("industry", formData.industry);
      if (formData.taxId) form.append("vat_registration", formData.taxId);
      if (formData.currency) form.append("currency_base", formData.currency);
      if (formData.timezone) form.append("time_zone", formData.timezone);
      if (formData.logoFile) form.append("logo", formData.logoFile);
      if (formData.notes) form.append("note", formData.notes);
      if (formData.payroll_computation_method) form.append("payroll_computation_method", formData.payroll_computation_method);
      if (formData.payee_remittance) form.append("payee_remittance", formData.payee_remittance);
      if (formData.wallet_account) form.append("wallet_account", formData.wallet_account);
      if (formData.wallet_bank_account) form.append("wallet_bank_account", formData.wallet_bank_account);
      if (formData.pay_day) form.append("pay_day", formData.pay_day);
      if (formData.payment_approval_workflow) form.append("payment_approval_workflow", formData.payment_approval_workflow);

      const res = await api.post("/v1/companies/update-company-info", withCompanyId(form));

      const ok = res?.data?.status === true || res?.data?.success === true;
      if (ok) {
        // Also save tax agents using the dedicated endpoint
        if (payeeTaxAgents && payeeTaxAgents.length > 0 && companyId) {
          const taxAgentIds = payeeTaxAgents.map(agent => agent.tax_agency_id).filter(Boolean);
          const stins = payeeTaxAgents.map(agent => agent.tax_id).filter(Boolean);
          
          if (taxAgentIds.length > 0 && stins.length > 0) {
            try {
              await api.post("/v1/company_tax_agent/create", {
                company_id: companyId,
                tax_agent_id: taxAgentIds,
                stin: stins
              });
            } catch (taxErr) {
              console.warn("Failed to save tax agents via dedicated endpoint", taxErr);
              // Continue anyway - they were saved in company info
            }
          }
        }

        await Swal.fire({
          icon: "success",
          title: "Success",
          text: res?.data?.message || "Organization profile updated",
          confirmButtonColor: "#16a34a",
        });
        // Update user context with new payroll_computation_method for instant effect
        updateUser({ ...user, company: { ...user.company, payroll_computation_method: formData.payroll_computation_method } });
        navigate("/settings/product-services");
      } else {
        await Swal.fire({
          icon: "error",
          title: "Update failed",
          text: res?.data?.message || "Could not update organization profile.",
          confirmButtonColor: "#16a34a",
        });
      }
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Server Error",
        text: err?.response?.data?.message || "Something went wrong. Please try again.",
        confirmButtonColor: "#16a34a",
      });
    } finally {
      setSaving(false);
    }
  };

const handleSubmit = async (e, formType) => {
  e.preventDefault();
  setSaving(true);

  try {
    const form = new FormData();

    let endpoint = "";

    // =========================
    // GENERAL SETTINGS
    // =========================
    if (formType === "generalSettings") {

      endpoint = "/v1/companies/update-company-info";

      form.append("name", formData.orgName || "");
      form.append("email", formData.email || "");
      form.append("phone_number", formData.phone || "");
      form.append("address", formData.address || "");
      const accountDetailsPayload = getAccountDetailsByPayload(formData.accountDetails, formData.accountName, formData.accountNumber, formData.bankName);
      
      // Send account details as arrays
      const accountNames = accountDetailsPayload.map((row) => row.accountName || "");
      const accountNumbers = accountDetailsPayload.map((row) => row.accountNumber || "");
      const bankNames = accountDetailsPayload.map((row) => row.bankName || "");
      
      accountNames.forEach((name) => form.append("account_name[]", name));
      accountNumbers.forEach((number) => form.append("account_number[]", number));
      bankNames.forEach((bank) => form.append("bank_name[]", bank));

      if (formData.industry)
        form.append("industry", formData.industry);

      if (formData.taxId)
        form.append("vat_registration", formData.taxId);

      if (formData.currency)
        form.append("currency_base", formData.currency);

      if (formData.timezone)
        form.append("time_zone", formData.timezone);

      if (formData.logoFile)
        form.append("logo", formData.logoFile);

      if (formData.notes)
        form.append("note", formData.notes);

      if (formData.payroll_computation_method)
        form.append(
          "payroll_computation_method",
          formData.payroll_computation_method
        );

      if (formData.payee_remittance)
        form.append("payee_remittance", formData.payee_remittance);

      if (formData.wallet_account)
        form.append("wallet_account", formData.wallet_account);

      if (formData.wallet_bank_account)
        form.append("wallet_bank_account", formData.wallet_bank_account);

      if (formData.pay_day)
        form.append("pay_day", formData.pay_day);

      if (formData.payment_approval_workflow)
        form.append("payment_approval_workflow", formData.payment_approval_workflow);

      form.append("auto_vat_deduction", normalizeAutoVatSetting(formData.auto_vat_deduction) ? "1" : "0");
    }

    // =========================
    // INCOME SETTINGS
    // =========================
    if (formType === "incomeSettings") {

      endpoint = "/v1/update_items_accounts";

      form.append(
        "income_account",
        formData.income_account || ""
      );
    }

    // =========================
    // RECEIVABLE SETTINGS
    // =========================
    if (formType === "receivableSettings") {

      endpoint = "/v1/update_items_accounts";

      form.append(
        "account_receivable",
        formData.receivable_account || ""
      );
    }

    // =========================
    // PAYABLE SETTINGS
    // =========================
    if (formType === "payableSettings") {

      endpoint = "/v1/update_items_accounts";

      form.append(
        "payable_gl",
        formData.payable_account || ""
      );
    }

    const res = await api.post(
      endpoint,
      withCompanyId(form)
    );

    const ok =
      res?.data?.status === true ||
      res?.data?.success === true;

    if (ok) {

      // SAVE TAX AGENTS ONLY FOR GENERAL SETTINGS
      if (
        formType === "generalSettings" &&
        payeeTaxAgents &&
        payeeTaxAgents.length > 0 &&
        companyId
      ) {

        const taxAgentIds = payeeTaxAgents
          .map((agent) => agent.tax_agency_id)
          .filter(Boolean);

        const stins = payeeTaxAgents
          .map((agent) => agent.tax_id)
          .filter(Boolean);

        if (taxAgentIds.length > 0 && stins.length > 0) {
          try {
            await api.post("/v1/company_tax_agent/create", {
              company_id: companyId,
              tax_agent_id: taxAgentIds,
              stin: stins,
            });
          } catch (taxErr) {
            console.warn(
              "Failed to save tax agents",
              taxErr
            );
          }
        }
      }

      await Swal.fire({
        icon: "success",
        title: "Success",
        text:
          res?.data?.message ||
          "Settings updated successfully",
        confirmButtonColor: "#16a34a",
      });

      // UPDATE USER ONLY FOR GENERAL SETTINGS
      if (formType === "generalSettings") {

        updateUser({
          ...user,
          company: {
            ...user.company,
            payroll_computation_method:
              formData.payroll_computation_method,
          },
        });

        navigate("/dashboard");
      }
    } else {

      await Swal.fire({
        icon: "error",
        title: "Update failed",
        text:
          res?.data?.message ||
          "Could not update settings.",
        confirmButtonColor: "#16a34a",
      });
    }

  } catch (err) {

    await Swal.fire({
      icon: "error",
      title: "Server Error",
      text:
        err?.response?.data?.message ||
        "Something went wrong. Please try again.",
      confirmButtonColor: "#16a34a",
    });

  } finally {
    setSaving(false);
  }
};
  
    const handleTypeSwitch = (newType) => {
    setType(newType);
  };

    useEffect(() => {
      const fetchAccountsData = async () => {
        setAccountsLoading(true);
        const companyId = localStorage.getItem("company_id") || localStorage.getItem("companyId");
  
        const unwrapList = (res) =>
          Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res?.data?.accounts)
                ? res.data.accounts
                : [];
  
        const fetchList = async (label, url, withCompanyParam = true) => {
          try {
            const res = await api.get(url, withCompanyParam ? { params: { company_id: companyId } } : undefined);
            const list = unwrapList(res);
            console.log(`${label} response:`, res?.data);
            return list;
          } catch (err) {
            console.error(`${label} fetch failed:`, err?.response?.status, err?.response?.data || err?.message);
            return [];
          }
        };
  
        try {
          // Run account fetches independently so one failure doesn't block others.
          const [incomeList, cogsList, inventoryList, taxList] = await Promise.all([
            fetchList("Income accounts", `/v1/get-income-accounts`),
            fetchList("COGS accounts", `/v1/get-expense-accounts`),
            fetchList("Inventory accounts", `/v1/get-inventory-accounts`),
            fetchList("Tax types", `/v1/get-tax-types`),
          ]);
  
          setIncomeAccounts(incomeList);
          setCogsAccounts(cogsList);
          setInventoryAccounts(inventoryList);
          setTaxTypes(taxList);
  
          // Payable endpoint is sometimes sensitive to query shape, so retry without params if needed.
          let payableList = await fetchList("Payable accounts", `/v1/get-payable-accounts`, true);
          if (!payableList.length) {
            payableList = await fetchList("Payable accounts (fallback no params)", `/v1/get-payable-accounts`, false);
          }

        let receivableList = await fetchList("Receivable accounts", `/v1/get-receivable-accounts`, true);
        if (!receivableList.length) {
          receivableList = await fetchList("Receivable accounts (fallback no params)", `/v1/get-receivables-accounts`, false);
        }

          setPayableAccounts(payableList);
          setReceivableAccounts(receivableList);
          console.log("Payable Accounts Set:", payableList);
        } catch (err) {
          console.error("Error fetching account data:", err);
        } finally {
          setAccountsLoading(false);
        }
      };
  
      fetchAccountsData();
    }, []);
  


  return (
    <>
      <HeaderNav />
      <div className={classes.container}>
          <div style={{ marginBottom: "2rem", borderBottom: "1.5px solid #16A34A" }}>
            <button
              type="button"
              className={type === "generalSettings" ? classes.activeTab : classes.inactiveTab}
              onClick={() => handleTypeSwitch("generalSettings")}
            >
              General Settings
            </button>
            <button
              type="button"
              className={type === "accountSettings" ? classes.activeTab : classes.inactiveTab}
              onClick={() => handleTypeSwitch("accountSettings")}
            >
              Default Account Settings
            </button>
          </div>
          {type === "generalSettings" && ( 
          <div className={classes.grid}>
            <section className={classes.card} aria-labelledby="org-settings">
              <div className={classes.previewHeader}>
                <div className={classes.previewLogoContainer} id="previewLogo">
                  {formData.logoDataUrl ? <img src={formData.logoDataUrl} alt="logo" className={classes.previewImg}/> : (
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zM12 14c-4.42 0-8 1.79-8 4v2h16v-2c0-2.21-3.58-4-8-4z" fill="#cbd5e1"/></svg>
                  )}
                </div>
                <div className={classes.previewInfo}>
                  <div className={classes.previewName} id="previewName">{formData.orgName || 'Your organization name'}</div>
                  <div className={classes.meta}>Industry — <span id="previewIndustryVal">{formData.industry || '—'}</span></div>
                </div>
              </div>
              <h1 id="org-settings">Organization Profile Settings</h1>
              <p className={classes.lead}>Update organization details — name, address, logo, contact and defaults for billing/timezone.</p>

              {/* <form id="orgForm" onSubmit={handleSubmit} autoComplete="off"> */}
              <form id="orgForm" onSubmit={(e) => handleSubmit(e, "generalSettings")} autoComplete="off">
                <div className={classes.field}>
                  <label htmlFor="orgName">Organization name</label>
                  <input id="orgName" name="orgName" type="text" placeholder="e.g. Brookes Professional Services Limited" required value={formData.orgName} onChange={handleChange} />
                </div>

                <div className={classes.row}>
                  <div className={classes.rowFlex}>
                    <label htmlFor="email">Support email</label>
                    <input id="email" name="email" type="email" placeholder="support@company.com" value={formData.email} onChange={handleChange} />
                  </div>
                  <div className={classes.rowNarrow}>
                    <label htmlFor="phone">Phone</label>
                    <input id="phone" name="phone" type="text" placeholder="+234 800 000 0000" value={formData.phone} onChange={handleChange} />
                  </div>
                </div>

                <div className={classes.field}>
                  <label htmlFor="address">Address</label>
                  <textarea id="address" name="address" placeholder="Street, City, State, Country" value={formData.address} onChange={handleChange} />
                </div>

                <div className={classes.row}>
                  <div className={classes.rowFlex}>
                    <label htmlFor="industry">Industry</label>
                    <select id="industry" name="industry" value={formData.industry} onChange={handleChange} disabled={loadingIndustries}>
                      <option value="">{loadingIndustries ? "Loading industries..." : "Select an industry"}</option>
                      {industries.map((ind) => (
                        <option key={ind.id || ind.name} value={ind.id}>
                          {ind.name || ind.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={classes.rowNarrow}>
                    <label htmlFor="taxId">Tax ID</label>
                    <input id="taxId" name="taxId" type="text" placeholder="TIN/VAT" value={formData.taxId} onChange={handleChange} />
                  </div>
                </div>

                <div className={classes.field}>
                  <label>Account details</label>
                  <div className={classes.accountRepeater}>
                    {formData.accountDetails.map((detail, index) => (
                      <div key={detail.id} className={classes.accountRepeaterRow}>
                        <div className={classes.grid2}>
                          <div className={classes.inputGroup}>
                            <label htmlFor={`accountName-${detail.id}`}>Account name</label>
                            <input
                              id={`accountName-${detail.id}`}
                              name={`accountName-${detail.id}`}
                              type="text"
                              placeholder="Brookes Professional Services Limited"
                              value={detail.accountName}
                              onChange={(e) => handleAccountDetailChange(detail.id, "accountName", e.target.value)}
                            />
                          </div>
                          <div className={classes.inputGroup}>
                            <label htmlFor={`accountNumber-${detail.id}`}>Account number</label>
                            <input
                              id={`accountNumber-${detail.id}`}
                              name={`accountNumber-${detail.id}`}
                              type="text"
                              placeholder="0123456789"
                              value={detail.accountNumber}
                              onChange={(e) => handleAccountDetailChange(detail.id, "accountNumber", e.target.value)}
                            />
                          </div>
                        </div>

                        <div className={classes.inputGroup}>
                          <label htmlFor={`bankName-${detail.id}`}>Bank name</label>
                          <select
                            id={`bankName-${detail.id}`}
                            name={`bankName-${detail.id}`}
                            value={detail.bankName}
                            onChange={(e) => handleAccountDetailChange(detail.id, "bankName", e.target.value)}
                          >
                            <option value="">-- Select bank --</option>
                            {banks.map((b) => (
                              <option key={`${b.code || b.name}-${detail.id}`} value={b.name || b.bank_name || ""}>
                                {b.name || b.bank_name}{b.code ? ` - ${b.code}` : ""}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className={classes.repeaterControls}>
                          <label className={classes.radioLabel}>
                            <input
                              type="radio"
                              name="defaultAccount"
                              checked={detail.isDefault}
                              onChange={() => handleAccountDetailDefault(detail.id)}
                            />
                            Default account
                          </label>
                          <button
                            type="button"
                            className={`${classes.btn} ${classes.btnDanger} ${classes.btnSmall}`}
                            onClick={() => handleRemoveAccountDetailRow(detail.id)}
                            disabled={formData.accountDetails.length === 1}
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={`${classes.btn} ${classes.btnPrimary}`}
                    onClick={handleAddAccountDetailRow}
                    style={{ marginTop: 8 }}
                  >
                    <Plus size={14} /> Add another account
                  </button>
                </div>


                <div className={classes.row}>
                  <div className={classes.rowFlex}>
                    <label htmlFor="currency">Default currency</label>
                    <select id="currency" name="currency" value={formData.currency} onChange={(e)=> setFormData(prev=>({...prev, currency: e.target.value}))}>
                      {currencyOptions.length === 0 ? (
                        <>
                          <option value="NGN">NGN — Nigerian Naira</option>
                          <option value="USD">USD — US Dollar</option>
                          <option value="EUR">EUR — Euro</option>
                        </>
                      ) : (
                        <>
                          {currencyOptions.map(c => (
                            <option key={c.id} value={c.id}>{(c.symbol? `${c.symbol} `: '')}{c.code}{c.country? ` - ${c.country}`:''}</option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>
                  <div className={classes.rowNarrow}>
                    <label htmlFor="timezone">Timezone</label>
                    <select id="timezone" name="timezone" value={formData.timezone} onChange={handleChange}>
                      {timezoneOptions.length === 0 ? (
                        <>
                          <option value="Africa/Lagos">Africa/Lagos</option>
                          <option value="UTC">UTC</option>
                        </>
                      ) : (
                        <>
                          {timezoneOptions.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </>
                      )}
                    </select>
                  </div>
                </div>

                <div className={classes.field}>
                  <label>Organization logo</label>
                  <div className={classes.logoDrop}>
                    <div className={classes.avatar} id="logoPreview" aria-hidden={formData.logoDataUrl? 'false':'true'}>
                      {formData.logoDataUrl ? <img src={formData.logoDataUrl} alt="logo"/> : (
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zM12 14c-4.42 0-8 1.79-8 4v2h16v-2c0-2.21-3.58-4-8-4z" fill="#cbd5e1"/></svg>
                      )}
                    </div>

                    <div className={classes.uploadControls}>
                      <div className={`${classes.small} ${classes.muted}`}>PNG, JPG up to 5MB. Recommended 400×400</div>
                      <div className={classes.uploadButtonGroup}>
                        <label className={`${classes.btn} ${classes.btnGhost}`} htmlFor="logoInput">Choose file</label>
                        <button type="button" id="removeLogo" className={classes.btn} onClick={handleRemoveLogo}>Remove</button>
                      </div>
                      <input id="logoInput" type="file" accept="image/*" className={classes.hidden} onChange={handleLogoChange} />
                    </div>
                  </div>
                </div>

                <div className={classes.field}>
                  <label htmlFor="notes">Notes / Invoice footer</label>
                  <textarea id="notes" name="notes" placeholder="Optional text that appears on invoices or member letters." value={formData.notes} onChange={handleChange}></textarea>
                </div>

                {/* payroll related fields */}
                <div className={classes.grid2}>
                  <div className={classes.field}>
                    <label htmlFor="payroll_computation_method">Computation record</label>
                    <select id="payroll_computation_method" name="payroll_computation_method" value={formData.payroll_computation_method} onChange={handleChange}>
                      <option value="">-- select --</option>
                      <option value="1">Direct</option>
                      <option value="2">Step - Grade</option>
                    </select>
                  </div>

                  <div className={classes.field}>
                    <label htmlFor="payment_approval_workflow">Payment Approval Workflow</label>
                    <select id="payment_approval_workflow" name="payment_approval_workflow" value={formData.payment_approval_workflow} onChange={handleChange}>
                      <option value="1">Deactivate</option>
                      <option value="2">Activate</option>
                    </select>
                  </div>
                </div>

                <div className={classes.grid2}>
                  <div className={classes.field}>
                    <label htmlFor="wallet_account">Wallet - Petty Cash Account</label>
                    <select id="wallet_account" name="wallet_account" value={formData.wallet_account} onChange={handleChange}>
                      {loadingCashAccounts ? (
                        <>
                          <option value="">Loading cash accounts...</option>
                        </>
                      ) : cashAccounts && cashAccounts.length ? (
                        <>
                          <option value="">-- select --</option>
                          {cashAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.gl_name || account.name || account.account_name || `Account ${account.id}`}
                            </option>
                          ))}
                        </>
                      ) : (
                        <>
                          <option value="">No cash accounts available</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className={classes.field}>
                    <label htmlFor="wallet_bank_account">Wallet - Bank Account</label>
                    <select id="wallet_bank_account" name="wallet_bank_account" value={formData.wallet_bank_account} onChange={handleChange}>
                      {loadingBankAccounts ? (
                        <>
                          <option value="">Loading bank accounts...</option>
                        </>
                      ) : bankAccounts && bankAccounts.length ? (
                        <>
                          <option value="">-- select --</option>
                          {bankAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.gl_name || account.name || account.account_name || `Account ${account.id}`}
                            </option>
                          ))}
                        </>
                      ) : (
                        <>
                          <option value="">No bank accounts available</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                {/* <div className={classes.field}>
                  <label htmlFor="payee_remittance">Payee remittance record</label>
                  <select id="payee_remittance" name="payee_remittance" value={formData.payee_remittance} onChange={handleChange}>
                    {loadingTaxAgencies ? (
                      <>
                        <option value="">Loading tax agencies...</option>
                      </>
                    ) : taxAgencies && taxAgencies.length ? (
                      <>
                        <option value="">-- select --</option>
                        {taxAgencies.map((agency) => (
                          <option key={agency.id} value={agency.id}>
                            {agency.name || agency.agency_name || `Agency ${agency.id}`}
                          </option>
                        ))}
                      </>
                    ) : (
                      <>
                        <option value="">No tax agencies available</option>
                      </>
                    )}
                  </select>
                </div> */}

                <div className={classes.grid2}>
                  <div className={classes.field}>
                    <label htmlFor="pay_day">Pay day</label>
                    <input id="pay_day" name="pay_day" type="date" value={formData.pay_day} onChange={handleChange} />
                  </div>
                  <div className={classes.field}>
                    <label htmlFor="auto_vat_deduction">Add VAT Automatically</label>
                    <div className={classes.toggleWrapper}>
                      <label className={classes.toggleLabel} htmlFor="auto_vat_deduction">
                        <input
                          id="auto_vat_deduction"
                          name="auto_vat_deduction"
                          type="checkbox"
                          checked={normalizeAutoVatSetting(formData.auto_vat_deduction)}
                          onChange={(e) => setFormData((prev) => ({ ...prev, auto_vat_deduction: e.target.checked ? "1" : "0" }))}
                        />
                        <span className={classes.toggleSwitch} />
                      </label>
                    </div>
                  </div>
                </div>

                {/* <div className={classes.field}>
                  <label>Payee Tax Agents</label>
                  <div className={classes.taxAgentsContainer}>
                    {payeeTaxAgents.map((row, index) => (
                      <div key={row.id} className={classes.taxAgentRow}>
                        <select 
                          value={row.tax_agency_id} 
                          onChange={(e) => handleTaxAgentChange(row.id, "tax_agency_id", e.target.value)}
                          className={classes.taxAgentSelect}
                        >
                          <option value="">-- Select tax agent --</option>
                          {taxAgencies && taxAgencies.map((agency) => (
                            <option key={agency.id} value={agency.id}>
                              {agency.name || agency.agency_name || `Agency ${agency.id}`}
                            </option>
                          ))}
                        </select>

                        <input
                          type="text"
                          value={row.tax_id}
                          onChange={(e) => handleTaxAgentChange(row.id, "tax_id", e.target.value)}
                          placeholder="Tax ID"
                          className={classes.taxIdInput}
                        />

                        <button
                          type="button"
                          onClick={() => handleGenerateTaxId(row.id)}
                          className={`${classes.btn} ${classes.btnSmall} ${classes.btnIcon}`}
                          title="Generate Tax ID"
                        >
                          <RefreshCw size={16} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleRemoveTaxAgentRow(row.id)}
                          className={`${classes.btn} ${classes.btnSmall} ${classes.btnDanger}`}
                          title="Remove row"
                        >
                          <Trash2 size={16} />
                        </button>

                        {index === payeeTaxAgents.length - 1 && (
                          <button
                            type="button"
                            onClick={handleAddTaxAgentRow}
                            className={`${classes.btn} ${classes.btnSmall} ${classes.btnPrimary}`}
                            title="Add another tax agent"
                          >
                            <Plus size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div> */}

                <div className={classes.actions}>
                  <button type="button" id="cancelBtn" className={`${classes.btn} ${classes.btnGhost}`} onClick={handleCancel}>Cancel</button>
                  <button type="submit" className={`${classes.btn} ${classes.btnPrimary}`} disabled={saving || loading}>{saving? 'Saving...': 'Save changes'}</button>
                </div>
              </form>

            </section>
            <aside className={`${classes.card} ${classes.previewCard}`} aria-labelledby="preview-heading">
              {/* <h3 id="preview-heading">Live preview</h3> */}
              
              {/* <div className={classes.previewHeader}>
                <div className={classes.previewLogoContainer} id="previewLogo">
                  {formData.logoDataUrl ? <img src={formData.logoDataUrl} alt="logo" className={classes.previewImg}/> : (
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zM12 14c-4.42 0-8 1.79-8 4v2h16v-2c0-2.21-3.58-4-8-4z" fill="#cbd5e1"/></svg>
                  )}
                </div>
                <div className={classes.previewInfo}>
                  <div className={classes.previewName} id="previewName">{formData.orgName || 'Your organization name'}</div>
                  <div className={classes.meta}>Industry — <span id="previewIndustryVal">{formData.industry || '—'}</span></div>
                </div>
              </div> */}
  <h3 id="preview-heading">PAYE Tax Agents</h3>
  <div className={classes.fieldtaxpaye}>
                  {/* <label>Payee Tax Agents</label> */}
                  <div className={classes.taxAgentsContainer}>
                    {payeeTaxAgents.map((row, index) => (
                      <div key={row.id} className={classes.taxAgentRow}>
                        <select 
                          value={row.tax_agency_id} 
                          onChange={(e) => handleTaxAgentChange(row.id, "tax_agency_id", e.target.value)}
                          className={classes.taxAgentSelect}
                        >
                          <option value="">-- Select tax agent --</option>
                          {taxAgencies && taxAgencies.map((agency) => (
                            <option key={agency.id} value={agency.id}>
                              {agency.name || agency.agency_name || `Agency ${agency.id}`}
                            </option>
                          ))}
                        </select>

                        <div className={classes.taxIdWrapper}>
                          <input
                            type="text"
                            value={row.tax_id}
                            onChange={(e) => handleTaxAgentChange(row.id, "tax_id", e.target.value)}
                            placeholder="Tax ID (10 digits)"
                            className={classes.taxIdInput}
                          />
                          {row.validation_message && (
                            <div className={`${classes.validationMessage} ${classes[`validation_${row.validation_status}`]}`}>
                              {row.validation_status === "invalid" || row.validation_status === "error" ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "space-between", width: "100%" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span>✗ {row.validation_message}</span>
                                    <button
                                      type="button"
                                      onClick={() => generateTaxIdForRow(row.id)}
                                      className={classes.generateLink}
                                      title="Generate Tax ID"
                                    >
                                      Generate tax id
                                    </button>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => clearValidationMessage(row.id)}
                                    className={classes.clearValidationBtn}
                                    title="Clear message"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "space-between", width: "100%" }}>
                                  <span>{row.validation_message}</span>
                                  <button
                                    type="button"
                                    onClick={() => clearValidationMessage(row.id)}
                                    className={classes.clearValidationBtn}
                                    title="Clear message"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveTaxAgentRow(row.id)}
                          className={`${classes.btn} ${classes.btnSmall} ${classes.btnDanger}`}
                          title="Remove row"
                        >
                          <Trash2 size={16} />
                        </button>

                        {index === payeeTaxAgents.length - 1 && (
                          <button
                            type="button"
                            onClick={handleAddTaxAgentRow}
                            className={`${classes.btn} ${classes.btnSmall} ${classes.btnPrimary}`}
                            title="Add another tax agent"
                          >
                            <Plus size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={submitTaxAgents}
                    className={`${classes.btn} ${classes.btnPrimary}`}
                    style={{ width: "100%", marginTop: "12px" }}
                  >
                    Submit Tax Agents
                  </button>
                </div>
              <div className={classes.divider}></div>
              {/* <div className={classes.previewContent}>
                <div className={`${classes.small} ${classes.muted}`}>Contact</div>
                <div id="previewContact" className={classes.previewMeta}>{(formData.email || 'support@company.com')} · {(formData.phone || '+234 800 000 0000')}</div>

                <div className={`${classes.small} ${classes.muted} ${classes.marginTop12}`}>Address</div>
                <div id="previewAddress" className={classes.previewMeta}>{formData.address || 'No address provided'}</div>

                <div className={`${classes.small} ${classes.muted} ${classes.marginTop12}`}>Defaults</div>
                <div className={`${classes.previewMeta} ${classes.marginTop6}`} id="previewDefaults">{formData.currency} · {formData.timezone}</div>
              </div> */}

              <div className={classes.previewActions}>
                <button className={`${classes.btn} ${classes.btnGhost}`} id="exportBtn" type="button" onClick={handleExport}>Export JSON</button>
                <button className={`${classes.btn} ${classes.btnDanger}`} id="resetBtn" type="button" onClick={handleReset}>Reset</button>
              </div>
            </aside>
          </div>
          )}
          {type === "accountSettings" && (
          <>
            <form autoComplete="off" onSubmit={(e) => handleSubmit(e, "incomeSettings")}>
              <div className={classes.card}>
                <h3>Account Income Settings</h3>
                {/* ACCOUNTING MAPPING */}
                <div className={classes.grid2} style={{ marginTop: 20 }}>
                  <div className={classes.inputGroup}>
                    <label htmlFor="income_account">Income Account</label>
                    <select
                      id="income_account"
                      name="income_account"
                      value={formData.income_account}
                      onChange={handleChange}
                      disabled={accountsLoading}
                    >
                      <option value="">Select Account</option>
                      {incomeAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.gl_name || account.name || account.account_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.actions}>
                    <button type="button" onClick={handleCancel} className={`${classes.btn} ${classes.btnGhost}`}>
                      Cancel
                    </button>
                    <button type="submit" className={`${classes.btn} ${classes.btnPrimary}`} disabled={saving}>
                      {saving ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
            <form autoComplete="off" onSubmit={(e) => handleSubmit(e, "receivableSettings")} style={{ marginTop: "2rem" }}>
              <div className={classes.card}>
                <h3>Credit Sales Settings</h3>
                {/* ACCOUNTING MAPPING */}
                <div className={classes.grid2} style={{ marginTop: 20 }}>
                  <div className={classes.inputGroup}>
                    <label htmlFor="receivable_account">Receivable Account</label>
                    <select
                      id="receivable_account"
                      name="receivable_account"
                      value={formData.receivable_account}
                      onChange={handleChange}
                      disabled={accountsLoading}
                    >
                      <option value="">Select Receivable Account</option>
                      {receivableAccounts.map((account) => (
                        <option
                          key={account.id || account.gl_name}
                          value={account.id || ""}
                        >
                          {account.gl_name || ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.actions}>
                    <button type="button" onClick={handleCancel} className={`${classes.btn} ${classes.btnGhost}`}>
                      Cancel
                    </button>
                    <button type="submit" className={`${classes.btn} ${classes.btnPrimary}`} disabled={saving}>
                      {saving ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
            <form autoComplete="off" onSubmit={(e) => handleSubmit(e, "payableSettings")} style={{ marginTop: "2rem" }}>
              <div className={classes.card}>
                <h3>Account Payable Settings</h3>
                {/* ACCOUNTING MAPPING */}
                <div className={classes.grid2} style={{ marginTop: 20 }}>
                  <div className={classes.inputGroup}>
                    <label htmlFor="payable_account">Payable Account</label>
                    <select
                      id="payable_account"
                      name="payable_account"
                      value={formData.payable_account}
                      onChange={handleChange}
                      disabled={accountsLoading}
                    >
                      <option value="">Select Payable Account</option>
                      {payableAccounts.map((account) => (
                        <option
                          key={account.id || account.gl_name}
                          value={account.id || ""}
                        >
                          {account.gl_name || ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={classes.footer}>
                  <div className={classes.actions}>
                    <button type="button" onClick={handleCancel} className={`${classes.btn} ${classes.btnGhost}`}>
                      Cancel
                    </button>
                    <button type="submit" className={`${classes.btn} ${classes.btnPrimary}`} disabled={saving}>
                      {saving ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
            <div className={classes.card} style={{ marginTop: "2rem" }}>
              <h3>Default Bank Account</h3>
              <p className={classes.lead} style={{ marginBottom: "1.5rem" }}>Select and manage your default bank account for company transactions.</p>
              
              {loadingCompanyBankAccounts ? (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <p>Loading bank accounts...</p>
                </div>
              ) : companyBankAccounts && companyBankAccounts.length > 0 ? (
                <div className={classes.bankAccountsList}>
                  {companyBankAccounts.map((account) => (
                    <div key={account.id || account.bank_account_id} className={classes.bankAccountItem} style={{
                      padding: "1rem",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      marginBottom: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: (account.default === 1 || account.default === true) ? "#f0fdf4" : "#fff"
                    }}>
                      <div>
                        <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>
                          {account.account_name || account.accountName || "Bank Account"}
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                          {account.account_number || account.accountNumber} • {account.bank_name || account.bankName}
                        </div>
                        {(account.default === 1 || account.default === true) && (
                          <div style={{ fontSize: "0.75rem", color: "#16a34a", marginTop: "0.25rem", fontWeight: "500" }}>
                            ✓ Default Account
                          </div>
                        )}
                      </div>
                      {(account.default !== 1 && account.default !== true) && (
                        <button
                          type="button"
                          className={`${classes.btn} ${classes.btnPrimary}`}
                          onClick={() => handleSetDefaultBankAccount(account.id || account.bank_account_id)}
                          style={{ marginLeft: "1rem", whiteSpace: "nowrap" }}
                        >
                          Set as Default
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: "2rem", textAlign: "center", color: "#64748b" }}>
                  <p>No bank accounts found. Please add a bank account in the general settings.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Payment Approval Workflow Modal */}
      {showPaymentWorkflowModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "32px",
            maxWidth: "600px",
            width: "90%",
            maxHeight: "80vh",
            overflowY: "auto",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px"
            }}>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#1a202c"
                }}>Payment Approval Workflow</h2>
                <p style={{
                  margin: "8px 0 0 0",
                  fontSize: "14px",
                  color: "#64748b"
                }}>Define and add the approval chain all payments must follow.</p>
              </div>
              <button
                onClick={handleCancelWorkflow}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#64748b",
                  padding: 0,
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                ×
              </button>
            </div>

            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "24px", marginBottom: "24px" }}>
              {workflowLevels.map((level, index) => (
                <div key={level.id} style={{ marginBottom: "16px" }}>
                  <label style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#475569",
                    marginBottom: "8px"
                  }}>
                    LEVEL {index + 1}
                  </label>
                  <div style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "flex-end"
                  }}>
                    <div style={{ flex: 1 }}>
                      <select
                        value={level.role_id}
                        onChange={(e) => handleLevelRoleChange(level.id, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          fontSize: "14px",
                          fontFamily: "inherit",
                          backgroundColor: "white",
                          cursor: "pointer",
                          boxSizing: "border-box"
                        }}
                      >
                        <option value="">Select a role</option>
                        {roles && roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name || role.role_name || `Role ${role.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => handleRemoveLevel(level.id)}
                      style={{
                        padding: "10px 12px",
                        backgroundColor: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#dc2626",
                        hover: { backgroundColor: "#fee2e2" }
                      }}
                      title="Delete this level"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}

              {workflowLevels.length === 0 && (
                <p style={{
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: "14px",
                  margin: "16px 0"
                }}>
                  No levels added yet
                </p>
              )}

              <button
                onClick={handleAddLevel}
                style={{
                  marginTop: "20px",
                  padding: "10px 16px",
                  backgroundColor: "#16a34a",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <Plus size={18} /> Add Level
              </button>
            </div>

            <div style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              borderTop: "1px solid #e2e8f0",
              paddingTop: "24px"
            }}>
              <button
                onClick={handleCancelWorkflow}
                style={{
                  padding: "10px 24px",
                  backgroundColor: "white",
                  color: "#475569",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveWorkflow}
                disabled={savingWorkflow}
                style={{
                  padding: "10px 24px",
                  backgroundColor: "#16a34a",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: savingWorkflow ? "not-allowed" : "pointer",
                  opacity: savingWorkflow ? 0.6 : 1
                }}
              >
                {savingWorkflow ? "Saving..." : "Save Workflow"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganizationSettings;
